import { MakerJSDownload, MimeType } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import groupBy from "lodash/groupBy";
import MakerJS, { type exporter, type IModel } from "makerjs";
import type { ProfileProperties } from "../interfaces/ProfileProperties";
import type { AngleSectionDefinition } from "../parts/AngleSectionDefinition";
import {
  getAngleSectionCutSign,
  normalizeCuts,
  normalizeCutsKey,
} from "../utils/angleSectionCuts";
import { calculateCommonProfileCenterValue } from "../utils/calculateProfileCenterValue";
import { determineProfilePropertiesDisplay } from "../utils/profileDisplayLogic";
import {
  boundingBoxFrom2DProfiles,
  calculateScale,
  calculateTextAngle,
  calculateTextPosition,
  distance2D,
  estimateSegmentLabelTextWidth,
  layoutPointFromProfile2D,
  makerJsCaptionAnchorAlongReadableText,
  profilePreviewInnerSizeMm,
  profilePreviewSegmentLabelOffsetMm,
  projectVector3ProfileTo2D,
} from "../utils/profilePreviewGeometry";

const ProfileLayerName = "profile";
const ProfileLayerNameCorner = "profileCorner";

type VectorAxis = "x" | "y" | "z";

// Axis mapping for different sides
const RectPropertiesBySide: Record<
  Sides,
  { horizontal: VectorAxis; vertical: VectorAxis }
> = {
  [Sides.FRONT]: { horizontal: "x", vertical: "y" },
  [Sides.BACK]: { horizontal: "x", vertical: "y" },
  [Sides.LEFT]: { horizontal: "z", vertical: "y" },
  [Sides.RIGHT]: { horizontal: "z", vertical: "y" },
  [Sides.TOP]: { horizontal: "x", vertical: "z" },
  [Sides.BOTTOM]: { horizontal: "x", vertical: "z" },
};

/**
 * Sort order for angle sections: B T H (x, z, y)
 */
const angleSectionSortOrderMap: Record<string, number> = {
  x: 0, // b
  y: 2, // h
  z: 1, // t
};

interface Profile2DRow {
  profile: ProfileProperties;
  points: Array<[number, number]>;
}

function collectProfileRows2D(profiles: ProfileProperties[]): Profile2DRow[] {
  return profiles
    .map((profile) => ({
      profile,
      points: projectVector3ProfileTo2D(profile.points, profile.cutReference),
    }))
    .filter((row) => row.points.length >= 2);
}

function layoutSegmentEndpoints(params: {
  p1: [number, number];
  p2: [number, number];
  bboxCenterU: number;
  bboxCenterV: number;
  scale: number;
  schemaWidthMm: number;
  schemaHeightMm: number;
}): { layout1: { x: number; y: number }; layout2: { x: number; y: number } } {
  const {
    p1,
    p2,
    bboxCenterU,
    bboxCenterV,
    scale,
    schemaWidthMm,
    schemaHeightMm,
  } = params;
  const [u1, v1] = p1;
  const [u2, v2] = p2;
  const base = {
    bboxCenterU,
    bboxCenterV,
    scale,
    schemaWidthMm,
    schemaHeightMm,
  };
  return {
    layout1: layoutPointFromProfile2D({ u: u1, v: v1, ...base }),
    layout2: layoutPointFromProfile2D({ u: u2, v: v2, ...base }),
  };
}

/** PDF-aligned segment label on a MakerJS line model (DXF export). */
function attachDxfProfileSegmentCaption(
  lineModel: IModel,
  layout1: { x: number; y: number },
  layout2: { x: number; y: number },
  segLen2d: number,
  dxfFontMm: number,
  labelOffsetMm: number,
): void {
  const lengthText = `${Math.round(segLen2d)}`;
  const midX = (layout1.x + layout2.x) / 2;
  const midY = (layout1.y + layout2.y) / 2;
  const dx = layout2.x - layout1.x;
  const dy = layout2.y - layout1.y;
  const segLayoutLen = Math.hypot(dx, dy);
  if (segLayoutLen <= 0) return;

  const { angleRad, flipOffset } = calculateTextAngle(dx, dy);
  const perpX = flipOffset ? dy / segLayoutLen : -dy / segLayoutLen;
  const perpY = flipOffset ? -dx / segLayoutLen : dx / segLayoutLen;
  const textWidth = estimateSegmentLabelTextWidth(lengthText.length, dxfFontMm);
  const textPos = calculateTextPosition({
    midX,
    midY,
    perpX,
    perpY,
    textWidth,
    fontSize: dxfFontMm,
    angleRad,
    offset: labelOffsetMm,
  });
  if (!textPos) return;

  const anchor = makerJsCaptionAnchorAlongReadableText({
    labelCenterX: textPos.x,
    labelCenterY: textPos.y,
    angleRad,
  });
  MakerJS.model.addCaption(lineModel, lengthText, anchor.origin, anchor.end);
}

interface AngleSectionWithCount extends AngleSectionDefinition {
  count: number;
}

/**
 * Creates a DXF download for aluminium profiles and angle sections
 */
export class ProfileDownloadGenerator {
  private readonly profiles: ProfileProperties[];
  private readonly angleSections: AngleSectionDefinition[];
  private readonly modelName: string;

  constructor(
    profiles: ProfileProperties[],
    angleSections: AngleSectionDefinition[],
    modelName: string,
  ) {
    if (profiles.length === 0) {
      throw new Error(
        "Cannot create ProfileDownloadGenerator with empty profiles array",
      );
    }
    this.profiles = profiles;
    this.angleSections = angleSections;
    this.modelName = modelName;
  }

  /**
   * Generate a Download object for profiles and angle sections
   */
  public async generateDownload(): Promise<ProfileMakerJSDownload> {
    return new ProfileMakerJSDownload(
      "Aluminium",
      MimeType.DXF,
      this.profiles,
      this.angleSections,
      this.modelName,
    );
  }
}

/**
 * MakerJS-based download implementation for profiles with angle sections
 */
class ProfileMakerJSDownload extends MakerJSDownload {
  public static readonly defaultDXFRenderOptions: exporter.IDXFRenderOptions = {
    layerOptions: {
      [ProfileLayerName]: { color: 250, fontSize: 2.8 },
      [ProfileLayerNameCorner]: { color: 250, fontSize: 3 },
      angleSection: { color: 250, fontSize: 3 },
      angleSectionHeader: { color: 250, fontSize: 6 },
      angleSectionOffset: { color: 250, fontSize: 2 },
      center: { color: 250, fontSize: 3 },
      centerSeeCase: { color: 250, fontSize: 7 },
      default: { color: 250, fontSize: 2.5 },
      header: { color: 250, fontSize: 3.5 },
      partSelectionHeader: { color: 250, fontSize: 6 },
    },
    fontSize: 2,
    units: "mm",
    usePOLYLINE: true,
  };

  private readonly profiles: ProfileProperties[];
  private readonly angleSections: AngleSectionDefinition[];
  private readonly modelName: string;

  constructor(
    title: string,
    mimeType: MimeType.DXF | MimeType.SVG,
    profiles: ProfileProperties[],
    angleSections: AngleSectionDefinition[],
    modelName: string,
  ) {
    super(title, mimeType, {
      dxfRenderOptions: ProfileMakerJSDownload.defaultDXFRenderOptions,
    });
    this.profiles = profiles;
    this.angleSections = angleSections;
    this.modelName = modelName;

    this.buildLayout();
  }

  /**
   * Build the complete layout with profiles and angle sections
   */
  private buildLayout(): void {
    // Add profile graphic
    this.model.addSubModel(
      this.createProfileGraphic(this.profiles, 200, 120),
      55 / 2,
      20,
      "profiles",
      0.17,
    );

    // Add layout structure
    this.model.addRect(0, 0, 55, 193.67);
    this.model.addLine(0, 49.42, 55, 49.42);
    this.model.addLine(0, 96.67, 55, 96.67);
    this.model.addLine(0, 143.67, 55, 143.67);
    this.model.addLine(0, 163.67, 55, 163.67);
    this.model.addLine(0, 178.67, 55, 178.67);

    // Add model name header
    const modelNameRegex = /^(.*?)\s(\d+x\d+x\d+)$|^(.+)$/;
    const match = modelNameRegex.exec(this.modelName);
    const line1 = match?.[1] ?? match?.[3] ?? this.modelName;
    const line2 = match?.[2];

    this.model.addCaption(line1, 55 / 2, 160, "header");
    if (line2) {
      this.model.addCaption(line2, 55 / 2, 154, "header");
    }

    // Add angle sections list
    this.addAngleSectionsList();

    // Part selection headline
    if (this.profiles[0]?.partSelection?.description) {
      this.model.addCaption(
        this.profiles[0].partSelection.description.replace(" inv", ""),
        55 / 2,
        42,
        "partSelectionHeader",
      );
      this.model.addRect(15, 38, 25, 9, "partSelectionHeader");
    }

    // Angle section headline
    if (this.angleSections[0]?.partSelection?.description) {
      this.model.addCaption(
        this.angleSections[0].partSelection.description,
        55 / 2,
        137,
        "angleSectionHeader",
      );
      this.model.addRect(12, 132, 31, 10, "angleSectionHeader");
    }
  }

  /**
   * Add angle sections list to the layout
   */
  private addAngleSectionsList(): void {
    const grouped = groupBy(this.angleSections, (as) =>
      [
        as.partSelection?.name,
        as.length,
        as.dir,
        as.cuts ? normalizeCutsKey(normalizeCuts(as.cuts)) : "none",
      ].join("-"),
    );

    const angleSectionsSorted = Object.values(grouped)
      .map((group) => {
        const first = group[0];
        if (!first) return null;
        return { ...first, count: group.length } as AngleSectionWithCount;
      })
      .filter((x): x is AngleSectionWithCount => x !== null)
      .sort((a, b) => {
        const aVal = angleSectionSortOrderMap[a.dir] ?? 0;
        const bVal = angleSectionSortOrderMap[b.dir] ?? 0;
        return aVal - bVal;
      });

    let n = 0;
    for (const angleSection of angleSectionsSorted) {
      const offset = this.calcRivetOffset(angleSection) / 10;

      this.model.addCaption(
        `${angleSection.count}x`,
        10,
        128 - n * 6,
        "angleSection",
      );
      this.model.addCaption(
        `${(angleSection.length / 10).toFixed(1)}`,
        23,
        128 - n * 6,
        "angleSection",
      );

      if (offset > 0) {
        this.model.addCaption(
          `${offset.toFixed(2)}`,
          37,
          128 - n * 6,
          "angleSectionOffset",
        );
      }

      const sign = angleSection.cuts
        ? getAngleSectionCutSign(normalizeCuts(angleSection.cuts))
        : "";
      if (sign) {
        this.model.addCaption(sign, 48, 128 - n * 6, "angleSection");
      }
      n++;
    }
  }

  /**
   * Calculate rivet offset for angle section
   */
  private calcRivetOffset(props: AngleSectionDefinition): number {
    const rivetDistance = 150;
    const minimumOffset = props.minimumOffset ?? 74;
    const minimumLengthForRivet = 2 * minimumOffset;

    if (props.length < minimumLengthForRivet) {
      return 0;
    }

    if (props.length <= rivetDistance + minimumLengthForRivet) {
      return props.length / 2;
    }

    let offset = (props.length % rivetDistance) / 2;
    while (offset < minimumOffset) {
      offset += 0.5 * rivetDistance;
    }
    return offset;
  }

  /**
   * Create a ProfileGraphic model
   * Uses the same logic as PDF profilePreviewSchema for consistency
   */
  private createProfileGraphic(
    profiles: ProfileProperties[],
    width: number,
    height: number,
  ): IModel {
    const model: IModel = { models: {} };

    // Use shared utility function for consistent display logic
    const displayResult = determineProfilePropertiesDisplay(profiles);

    // If more than 2 cutReferences, show "Siehe Case"
    if (displayResult.showSieheCase) {
      // L-case / 3D-case -> "Siehe Case"
      // Add a rect for centering (needed for MakerJSModel, same as old ProfileGraphic)
      model.models ??= {};
      model.models.rect = MakerJS.model.move(
        new MakerJS.models.Rectangle(0, 0),
        [0, 0],
      );

      // Add centered caption - same position as old ProfileGraphic
      const caption: IModel = {};
      MakerJS.model.addCaption(caption, "Siehe Case", [0, -10], [0, -10]);
      caption.layer = "centerSeeCase";
      model.models.caption = caption;
      return model;
    }

    // Draw only the first group
    if (displayResult.profilesToDisplay.length > 0) {
      this.renderProfiles(
        model,
        displayResult.profilesToDisplay,
        width,
        height,
      );
    }

    return model;
  }

  /**
   * One profile segment with layer (from 3D horizontal bounds) and 2D length in mm.
   */
  private resolveProfileDxfSegment(
    profile: ProfileProperties,
    points2D: Array<[number, number]>,
    segmentIndex: number,
    horizontal: VectorAxis,
    minX: number,
    maxX: number,
  ): {
    p1: [number, number];
    p2: [number, number];
    layer: string;
    segLen2d: number;
  } | null {
    const p1 = points2D[segmentIndex];
    const p2 = points2D[segmentIndex + 1];
    if (!p1 || !p2) return null;

    const point1 = profile.points[segmentIndex];
    const point2 = profile.points[segmentIndex + 1];
    if (!point1 || !point2) return null;

    const segLen2d = distance2D(p1, p2);
    if (segLen2d <= 0) return null;

    const layer = this.determineLayer(
      point1[horizontal],
      point2[horizontal],
      minX,
      maxX,
    );
    return { p1, p2, layer, segLen2d };
  }

  /**
   * Render profiles into the model
   * Scaling and segment labels match {@link profilePreviewSchema} (PDF).
   */
  private renderProfiles(
    model: IModel,
    profiles: ProfileProperties[],
    width: number,
    height: number,
  ): void {
    const firstProfile = profiles[0];
    if (!firstProfile) return;

    const rectProperties = RectPropertiesBySide[firstProfile.cutReference];
    if (!rectProperties) return;

    const { horizontal } = rectProperties;
    const allPoints2D = collectProfileRows2D(profiles);
    if (allPoints2D.length === 0) return;

    const bbox = boundingBoxFrom2DProfiles(
      allPoints2D.map((row) => ({ points: row.points })),
    );
    if (bbox.width <= 0 && bbox.height <= 0) return;

    const { maxW, maxH } = profilePreviewInnerSizeMm(width, height);
    const scale = calculateScale(bbox.width, bbox.height, maxW, maxH);
    const bboxCenterU = (bbox.minX + bbox.maxX) / 2;
    const bboxCenterV = (bbox.minY + bbox.maxY) / 2;

    this.addCenterCaptionFrom2D(model, allPoints2D, width, height);

    const { minX, maxX } = this.findXBounds(profiles, horizontal);
    const dxfFontMm =
      ProfileMakerJSDownload.defaultDXFRenderOptions.layerOptions?.[
        ProfileLayerName
      ]?.fontSize ?? 2.8;
    const labelOffsetMm = profilePreviewSegmentLabelOffsetMm();

    const layoutBase = {
      bboxCenterU,
      bboxCenterV,
      scale,
      schemaWidthMm: width,
      schemaHeightMm: height,
    };

    let lineIndex = 0;
    for (const { profile, points: points2D } of allPoints2D) {
      for (let p = 0; p < points2D.length - 1; p++) {
        const resolved = this.resolveProfileDxfSegment(
          profile,
          points2D,
          p,
          horizontal,
          minX,
          maxX,
        );
        if (!resolved) continue;

        const { layout1, layout2 } = layoutSegmentEndpoints({
          p1: resolved.p1,
          p2: resolved.p2,
          ...layoutBase,
        });

        const lineModel: IModel = {
          layer: resolved.layer,
          paths: {
            line: new MakerJS.paths.Line(
              [layout1.x, layout1.y],
              [layout2.x, layout2.y],
            ),
          },
        };

        attachDxfProfileSegmentCaption(
          lineModel,
          layout1,
          layout2,
          resolved.segLen2d,
          dxfFontMm,
          labelOffsetMm,
        );

        model.models ??= {};
        model.models[`line_${lineIndex}`] = lineModel;
        lineIndex++;
      }
    }
  }

  /**
   * Center total length caption (same string as PDF: {@code | cm |}).
   */
  private addCenterCaptionFrom2D(
    model: IModel,
    allPoints2D: Profile2DRow[],
    width: number,
    height: number,
  ): void {
    const profilesForCalculation = allPoints2D.map(({ profile, points }) => {
      const segmentLengths: number[] = [];
      for (let i = 0; i < points.length - 1; i++) {
        const a = points[i];
        const b = points[i + 1];
        if (a && b) segmentLengths.push(distance2D(a, b));
      }
      return {
        pointCount: profile.points.length,
        segmentLengths,
      };
    });

    const centerValue = calculateCommonProfileCenterValue(
      profilesForCalculation,
    );

    if (centerValue !== null) {
      const displayValue = (Math.floor(centerValue) / 10).toFixed(1);
      const centerCaption: IModel = {};
      MakerJS.model.addCaption(
        centerCaption,
        `| ${displayValue} |`,
        [width / 2 - 25, height / 2],
        [width / 2 + 25, height / 2],
      );
      centerCaption.layer = "center";
      model.models ??= {};
      model.models.centerCaption = centerCaption;
    }
  }

  /**
   * Find X bounds in profiles
   */
  private findXBounds(
    profiles: ProfileProperties[],
    horizontal: VectorAxis,
  ): { minX: number; maxX: number } {
    let minX = Infinity;
    let maxX = -Infinity;

    for (const profile of profiles) {
      for (const point of profile.points) {
        const x = point[horizontal];
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
      }
    }

    return { minX, maxX };
  }

  /**
   * Determine layer based on position
   */
  private determineLayer(
    x1: number,
    x2: number,
    minX: number,
    maxX: number,
  ): string {
    if (
      x1 !== x2 &&
      (x1 === minX || x2 === minX) !== (x1 === maxX || x2 === maxX)
    ) {
      return ProfileLayerNameCorner;
    }
    return ProfileLayerName;
  }
}
